import { OmniElement, html, css } from 'omni-ui';
import { Om2OptionsList } from './om2-options-list.js';
import { formComponent } from './form-component.js';
import { sleep } from './om2-util.js';

Om2OptionsList.register();

/*
 * Similar two om2-dropdown in usage, but UI uses two panes with the left side to
 * display options and the right side to show what is selected.
 */

export class Om2TwoPaneSelect extends Om2OptionsList {
  constructor() {
    super();
    this.isMulti = true;

    this.filteredOptions = [];
    this.search = '';

    // Lots of optional labels
    this.leftPaneLabel = '';
    this.rightPaneLabel = '';
    this.searchPlaceholder = '';
    this.leftPanePlaceholder = '';
    this.rightPanePlaceholder = '';

    this.sort = this.sortByName.bind(this);
  }

  static get properties() {
    return {
      /* Allow adding a new option if the search has no result  */
      addOptionFromSearch: { type: Object },

      /* Provide an async custom search function. Takes a search string and
       * returns a list of matching options. Can be used for fetch/ajax etc.
       */
      asyncSearch: { type: Function },

      /* Optional labels */
      leftPaneLabel: { type: String },
      rightPaneLabel: { type: String },
      leftPanePlaceholder: { type: String },
      rightPanePlaceholder: { type: String },
      searchPlaceholder: { type: String },

      /* Properties from parent class (Om2OptionsList):
       *
       * hasObjectValues
       * isMulti
       * options
       * optionsTree
       * collapsible
       * value
       * valueLabel
       * idProperty
       * nameProperty
       * childrenProperty
       * sort
       */

      /* Below are for internal use */
      isActive: { attribute: false },
      search: { attribute: false },
      loading: { state: true },
      filteredOptions: { attribute: false },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .left-pane,
        .right-pane {
          height: 272px;
        }
        .scrollable-list {
          overflow-y: auto;
          overflow-x: hidden;
          height: 100%;
        }
        .pane-placeholder {
          display: flex;
          justify-content: center;
          color: rgb(var(--rgb-core-medium));
          margin-top: 57px;
          text-align: center;
        }
        .right-pane .pane-placeholder {
          margin-top: 92px;
        }
        .left-pane {
          box-shadow: 17px 0px 0px -15px rgb(var(--rgb-border));
        }
        div.search omni-icon[icon-id='omni:interactive:search'] {
          --color-icon-lines: rgb(var(--rgb-core-medium));
        }
        div.search omni-icon[icon-id='omni:interactive:close'] {
          cursor: pointer;
          pointer-events: all;
        }
        *:focus {
          outline: none;
        }
        div.dropdown-item.add-new-option {
          border-radius: 10px;
          box-shadow: rgb(1 33 78 / 37%) 0px 24px 31px -6px,
            rgb(1 33 78 / 10%) 0px 2px 9px -1px;
          border: solid 1px var(--color-color-core-medium-two);
          padding-left: 1.286rem;
        }
        #loader-span {
          display: inline-block;
          height: 20px;
          width: 20px;
          margin: 10px;
        }
      `,
    ];
  }

  static register(name = 'om2-two-pane-select') {
    OmniElement.register(`${name}-impl`, Om2TwoPaneSelect);
    return OmniElement.register(name, formComponent(Om2TwoPaneSelect));
  }

  shouldUpdate(changed) {
    if (changed.has('search') || changed.has('options')) {
      if (this.search.length > 0) {
        this.getSearchResults();
      } else {
        this.resetSearch();
      }
    }

    return super.shouldUpdate(changed);
  }

  firstUpdated() {
    super.firstUpdated();
    this.resetSearch();
  }

  sortByName(a, b) {
    const getVal = opt => opt[this.nameProperty];
    if (getVal(a) > getVal(b)) return 1;
    if (getVal(a) < getVal(b)) return -1;
    return 0;
  }

  async getSearchResults() {
    if (this.asyncSearch) {
      this.lastSearchId = (this.lastSearchId || 0) + 1;
      const id = this.lastSearchId;
      const searchString = this.search;

      this.loading = true;
      await sleep(1000);
      if (id === this.lastSearchId) {
        // Still latest search
        const result = await this.asyncSearch(searchString);
        if (id === this.lastSearchId) {
          // Still latest search
          this.loading = false;
          this.filteredOptions = result.map(item => {
            let itemObj = {};
            if (typeof item === 'object') {
              itemObj = item;
            } else {
              itemObj[this.nameProperty] = item;
              itemObj[this.idProperty] = item;
            }
            return itemObj;
          });
        }
      }
      return;
    }

    this.filteredOptions = this.options.filter(opt =>
      opt[this.nameProperty].toLowerCase().includes(this.search.toLowerCase())
    );
  }

  resetSearch() {
    if (!this.initialized) return;
    this.shadowRoot.querySelector('#input-search').value = '';
    this.search = '';
    this.loading = false;
    this.lastSearchId += 1;
    this.filteredOptions = [...this.options];
  }

  renderPlaceholder(side) {
    return html`<div class="pane-placeholder">
      <span>${super.renderPlaceholder(this[`${side}PanePlaceholder`])}</span>
    </div>`;
  }

  renderPane(options, isLeftPane) {
    let extraOption = '';
    if (isLeftPane && this.loading) {
      return html` <div class="has-text-centered">
        <span id="loader-span" class="loader"></span>
      </div>`;
    }

    if (
      isLeftPane &&
      (this.addOptionFromSearch?.visible?.(this.search, options, this) ??
        (this.addOptionFromSearch && options.length === 0))
    ) {
      const label =
        this.addOptionFromSearch.label?.(this.search) ?? `Add "${this.search}"`;
      const addNew = async () => {
        this.addOptionFromSearch.callback(this.search, this);
        this.resetSearch();
      };

      extraOption = html`
        <div
          tabindex="0"
          class="dropdown-item is-clickable add-new-option"
          @click=${addNew}
          @keyup=${e => e.key === 'Enter' && addNew()}
        >
          ${label}
        </div>
      `;
    }

    return html`
      ${extraOption}
      <div class="scrollable-list">
        ${this.renderOptionsList(
          isLeftPane ? options : options.slice().sort(this.sort),
          { flat: !isLeftPane }
        )}
      </div>
    `;
  }

  render() {
    return html` <omni-style>
      <div class="columns">
        <div
          class="column left-pane is-flex is-flex-direction-column"
          @focusout=${async () => {
            // Wait for active element, then check if focus is out of left pane
            await sleep(0);
            const leftPane = this.shadowRoot.querySelector('.left-pane');
            if (!leftPane.contains(this.shadowRoot.activeElement)) {
              this.isActive = false;
            }
          }}
        >
          ${this.leftPaneLabel
            ? html`<div class="mb-2">
                <span class="subtitle">${this.leftPaneLabel}</span>
              </div>`
            : ''}
          <div class="search control has-icons-left has-icons-right mb-2">
            <input
              id="input-search"
              class="input"
              type="text"
              placeholder="${super.renderPlaceholder(this.searchPlaceholder)}"
              @focus=${() => {
                this.isActive = true;
              }}
              @keyup=${e => {
                this.search = e.target.value;
              }}
            />

            <span class="icon is-small is-left">
              <omni-icon
                class="is-size-1"
                icon-id="omni:interactive:search"
              ></omni-icon>
            </span>

            <span
              class="icon is-clickable is-small is-right ${this.search.length
                ? ''
                : 'is-hidden'}"
            >
              <omni-icon
                class="is-size-1"
                icon-id="omni:interactive:close"
                @click=${this.resetSearch}
              ></omni-icon>
            </span>
          </div>

          ${this.search.length === 0 &&
          !this.isActive &&
          this.leftPanePlaceholder
            ? this.renderPlaceholder('left')
            : this.renderPane(this.filteredOptions, true)}
        </div>
        <div class="column right-pane is-flex is-flex-direction-column">
          ${this.rightPaneLabel
            ? html`<div class="mb-2">
                <span class="subtitle">${this.rightPaneLabel}</span>
              </div>`
            : ''}
          ${this.selected.length === 0
            ? this.renderPlaceholder('right')
            : this.renderPane(this.selected, false)}
        </div>
      </div>
    </omni-style>`;
  }
}

Om2TwoPaneSelect.register();
